<template>
  <v-card
    outlined
    class="pa-3"
  >
    <h4 class="subtitle-2 font-weight-bold mb-3">
      {{ $t('admin.productsDatabase.importWizard.steps.productDetails.authors') }}
    </h4>
    
    <div
      v-if="value.length > 0"
      class="mb-3"
    >
      <v-chip
        v-for="(authorData, index) in value"
        :key="`author-${index}`"
        class="mr-2 mb-2"
        close
        @click:close="removeAuthor(index)"
      >
        {{ authorData.author.name }} ({{ authorData.role || 'Author' }})
      </v-chip>
    </div>

    <v-row>
      <v-col
        cols="12"
        sm="7"
      >
        <AuthorSearch
          v-model="selectedAuthor"
          :label="$t('admin.productsDatabase.importWizard.steps.productDetails.searchAuthors')"
          @author-selected="handleAuthorSelected"
        />
      </v-col>
      
      <v-col
        cols="12"
        sm="5"
      >
        <AuthorRoleSelector
          v-model="selectedRole"
          :label="$t('admin.productsDatabase.importWizard.steps.productDetails.authorRoleField')"
          @role-selected="handleRoleSelected"
        />
      </v-col>
    </v-row>
    
    <v-btn
      color="primary"
      :disabled="!selectedAuthor"
      small
      @click="addAuthor"
    >
      <v-icon
        left
        small
      >
        mdi-plus
      </v-icon>
      {{ $t('admin.productsDatabase.importWizard.steps.productDetails.addAuthor') }}
    </v-btn>
  </v-card>
</template>

<script>
import AuthorSearch from '@/components/AuthorSearch.vue';
import AuthorRoleSelector from '@/components/AuthorRoleSelector.vue';
import AuthorRolesMixin from '@/mixins/AuthorRolesMixin';
import BookAuthorMixin from '@/mixins/BookAuthorMixin';

export default {
  name: 'AuthorSelectionPanel',
  components: {
    AuthorSearch,
    AuthorRoleSelector
  },
  mixins: [AuthorRolesMixin, BookAuthorMixin],
  props: {
    value: {
      type: Array,
      default: () => []
    },
    bookId: {
      type: String,
      default: ''
    }
  },
  emits: ['input', 'author-added', 'author-removed'],
  data() {
    return {
      selectedAuthor: null,
      selectedRole: 'author',
      selectedRoleObj: null
    };
  },
  created() {
    this.fetchAuthorRoles();
  },
  methods: {
    handleAuthorSelected(author) {
      this.selectedAuthor = author;
    },
    
    handleRoleSelected(roleObj) {
      this.selectedRoleObj = roleObj;
    },
    
    async addAuthor() {
      if (!this.selectedAuthor) {
        return;
      }

      try {
        const roleObj = this.selectedRoleObj || 
                      this.authorRoles.find(role => role._id === this.selectedRole) || 
                      { _id: 'author', id: 'author_roles/author', description: 'Author', localizedDescription: 'Author' };

        if (!this.bookId) {
          console.error('No book ID available to add author to');
          throw new Error('No book ID available to add author to');
        }
        
        const authorToAdd = { ...this.selectedAuthor };
        this.selectedAuthor = null;
        
        if (this.bookId) {
          const newBookAuthor = await this.addBookAuthor(
            this.bookId,
            authorToAdd,
            roleObj,
            this.value.length + 1
          );
          
          if (newBookAuthor) {
            const existingIndex = this.value.findIndex(item => 
              item.author.id === authorToAdd.id
            );
            
            const newAuthorData = {
              id: newBookAuthor.id,
              author: newBookAuthor.author,
              role: newBookAuthor.role.description || roleObj.description,
              sequence: newBookAuthor.sequence
            };
            
            const updatedAuthors = [...this.value];
            
            if (existingIndex >= 0) {
              updatedAuthors[existingIndex] = newAuthorData;
            } else {
              updatedAuthors.push(newAuthorData);
            }
            
            this.$emit('input', updatedAuthors);
            this.$emit('author-added', newAuthorData);
            
            this.$store.dispatch('snackbar/showSnackbar', {
              text: this.$t('admin.productsDatabase.importWizard.steps.productDetails.authorAddSuccess', 'Author added successfully'),
              color: 'success'
            });
          }
        } else {
          const roleDescription = roleObj.localizedDescription || roleObj.description;
          
          const newAuthorData = {
            id: `temp-${Date.now()}`,
            author: authorToAdd,
            role: roleDescription,
            sequence: this.value.length + 1
          };
          
          const existingIndex = this.value.findIndex(item => 
            item.author.id === authorToAdd.id
          );
          
          const updatedAuthors = [...this.value];
          
          if (existingIndex >= 0) {
            updatedAuthors[existingIndex] = newAuthorData;
          } else {
            updatedAuthors.push(newAuthorData);
          }
          
          this.$emit('input', updatedAuthors);
          this.$emit('author-added', newAuthorData);
        }
      } catch (error) {
        console.error('Error adding author:', error);
        this.$store.dispatch('snackbar/showSnackbar', {
          text: this.$t('admin.productsDatabase.importWizard.steps.productDetails.authorAddError', 'Error adding author'),
          color: 'error'
        });
      }
    },
    
    async removeAuthor(index) {
      if (index >= 0 && index < this.value.length) {
        try {
          const authorToRemove = this.value[index];
          
          if (this.bookId && authorToRemove.id && !authorToRemove.id.startsWith('temp-')) {
            await this.removeBookAuthor(
              authorToRemove.id, 
              this.bookId
            );
          }
          
          const updatedAuthors = [...this.value];
          const removedAuthor = updatedAuthors.splice(index, 1)[0];
          
          this.$emit('input', updatedAuthors);
          this.$emit('author-removed', removedAuthor);
          
          this.$store.dispatch('snackbar/showSnackbar', {
            text: this.$t('admin.productsDatabase.importWizard.steps.productDetails.authorRemoveSuccess', 'Author removed successfully'),
            color: 'success'
          });
        } catch (error) {
          console.error('Error removing author:', error);
          this.$store.dispatch('snackbar/showSnackbar', {
            text: this.$t('admin.productsDatabase.importWizard.steps.productDetails.authorRemoveError', 'Error removing author'),
            color: 'error'
          });
        }
      }
    }
  }
};
</script>
